import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Navbar.css';
import { categories, getProductsByCategory } from '../data/productsData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsDropdownOpen, setIsProductsDropdownOpen] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(categories[0]?.slug || null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [openCategories, setOpenCategories] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleProductsDropdown = () => {
    setIsProductsDropdownOpen(!isProductsDropdownOpen);
  };

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsProductsDropdownOpen(false);
    setOpenCategories({});
  };

  const toggleCategory = (categorySlug) => {
    setOpenCategories(prev => ({
      ...prev,
      [categorySlug]: !prev[categorySlug]
    }));
  };

  const handleCategoryHover = (categorySlug) => {
    if (!isMobile) {
      setHoveredCategory(categorySlug);
    }
  };

  const handleMegaMenuEnter = () => {
    if (!isMobile) {
      setIsProductsDropdownOpen(true);
    }
  };

  const handleMegaMenuLeave = () => {
    if (!isMobile) {
      setIsProductsDropdownOpen(false);
      setHoveredCategory(categories[0]?.slug || null);
    }
  };

  // Get products for currently hovered category
  const displayedProducts = hoveredCategory ? getProductsByCategory(hoveredCategory) : [];

  const navLinks = [
    { name: 'Home', path: '/', type: 'link' },
    { name: 'About', path: '/about', type: 'link' },
    { name: 'Products', type: 'dropdown' },
    { name: 'Quality', path: '/quality', type: 'link' },
    { name: 'Sustainability', path: '/sustainability', type: 'link' },
    { name: 'Contact', path: '/contact', type: 'link' }
  ];

  return (
    <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>
      <div className="navbar-container">
        <div className="navbar-logo">
          <Link to="/">we2 exporters</Link>
        </div>

        <div className={`navbar-menu ${isMobileMenuOpen ? 'active' : ''}`}>
          {navLinks.map((link, index) => {
            if (link.type === 'dropdown') {
              // MOBILE: Simple vertical accordion
              if (isMobile) {
                return (
                  <div key={index} className="navbar-dropdown-mobile">
                    <button
                      className="navbar-link navbar-dropdown-toggle-mobile"
                      onClick={toggleProductsDropdown}
                    >
                      {link.name}
                      <span className={`dropdown-arrow ${isProductsDropdownOpen ? 'open' : ''}`}>▼</span>
                    </button>
                    
                    {isProductsDropdownOpen && (
                      <div className="mobile-categories-list">
                        {categories.map((category) => {
                          const categoryProducts = getProductsByCategory(category.slug);
                          return (
                            <div key={category.id} className="mobile-category">
                              <div className="mobile-category-header">
                                <Link
                                  to={`/products/${category.slug}`}
                                  onClick={closeMenus}
                                  className="mobile-category-link"
                                >
                                  {category.name}
                                </Link>
                                <button
                                  className={`mobile-arrow ${openCategories[category.slug] ? 'open' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleCategory(category.slug);
                                  }}
                                  aria-label={`Toggle ${category.name} products`}
                                >
                                  ▼
                                </button>
                              </div>
                              
                              {openCategories[category.slug] && (
                                <div className="mobile-products">
                                  {categoryProducts.map((product) => (
                                    <Link
                                      key={product.id}
                                      to={`/products/${category.slug}/${product.id}`}
                                      className="mobile-product-link"
                                      onClick={closeMenus}
                                    >
                                      {product.name}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              // DESKTOP: Mega menu with hover
              return (
                <div
                  key={index}
                  className="navbar-dropdown navbar-mega-dropdown"
                  onMouseEnter={handleMegaMenuEnter}
                  onMouseLeave={handleMegaMenuLeave}
                >
                  <button
                    className="navbar-link navbar-dropdown-toggle"
                    onClick={toggleProductsDropdown}
                  >
                    {link.name}
                    <span className={`dropdown-arrow ${isProductsDropdownOpen ? 'open' : ''}`}>▼</span>
                  </button>
                  
                  <div className={`navbar-mega-menu ${isProductsDropdownOpen ? 'show' : ''}`}>
                    <div className="mega-menu-container">
                      {/* Left Column - Categories */}
                      <div className="mega-menu-categories">
                        <h3 className="mega-menu-heading">Categories</h3>
                        <ul className="category-list">
                          {categories.map((category) => (
                            <li 
                              key={category.id}
                              className={`category-item ${hoveredCategory === category.slug ? 'active' : ''}`}
                            >
                              <Link
                                to={`/products/${category.slug}`}
                                className="category-link"
                                onMouseEnter={() => handleCategoryHover(category.slug)}
                                onClick={closeMenus}
                              >
                                {category.name}
                                <span className="category-arrow">→</span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Right Column - Products */}
                      <div className="mega-menu-products">
                        <h3 className="mega-menu-heading">
                          {categories.find(cat => cat.slug === hoveredCategory)?.name || 'Products'}
                        </h3>
                        <ul className="product-list">
                          {displayedProducts.map((product) => (
                            <li key={product.id} className="product-item">
                              <Link
                                to={`/products/${hoveredCategory}/${product.id}`}
                                className="product-link"
                                onClick={closeMenus}
                              >
                                {product.name}
                              </Link>
                            </li>
                          ))}
                          {displayedProducts.length === 0 && (
                            <li className="no-products">No products available</li>
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }
            
            return (
              <Link
                key={index}
                to={link.path}
                className="navbar-link"
                onClick={closeMenus}
              >
                {link.name}
              </Link>
            );
          })}
        </div>

        <div className="navbar-hamburger" onClick={toggleMobileMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
